import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { 
  Plus, Pencil, Trash2, Loader2, GripVertical, Car, Truck, 
  CheckCircle2, XCircle, Save, AlertCircle
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// Main categories
const MAIN_CATEGORIES = [
  { id: "furgone", label: "Furgone", icon: Truck },
  { id: "auto", label: "Auto", icon: Car },
  { id: "altro", label: "Altro", icon: Truck },
];

export default function AdminVehicleCategories() {
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    id: "",
    label: "",
    description: "",
    category: "furgone",
    order: 99,
    is_active: true
  });
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  useEffect(() => {
    if (token) {
      fetchCategories();
    }
  }, [token]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/admin/vehicle-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(res.data);
    } catch (error) {
      toast.error("Errore nel caricamento categorie");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        id: category.id,
        label: category.label,
        description: category.description || "",
        category: category.category || "furgone",
        order: category.order || 99,
        is_active: category.is_active !== false
      });
    } else {
      setEditingCategory(null);
      setFormData({
        id: "",
        label: "",
        description: "",
        category: "furgone",
        order: categories.length + 1,
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCategory(null);
    setFormData({
      id: "",
      label: "",
      description: "",
      category: "furgone",
      order: 99,
      is_active: true
    });
  };

  const handleSave = async () => {
    // Validation
    if (!formData.id.trim() || !formData.label.trim()) {
      toast.error("ID e Nome sono obbligatori");
      return;
    }

    // Format ID: lowercase, no spaces
    const formattedId = formData.id.toLowerCase().replace(/\s+/g, "_");

    setSaving(true);
    try {
      if (editingCategory) {
        // Update existing
        await axios.put(
          `${API}/admin/vehicle-categories/${editingCategory.id}`,
          {
            label: formData.label,
            description: formData.description,
            category: formData.category,
            order: parseInt(formData.order),
            is_active: formData.is_active
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Categoria aggiornata!");
      } else {
        // Create new
        await axios.post(
          `${API}/admin/vehicle-categories`,
          {
            ...formData,
            id: formattedId,
            order: parseInt(formData.order)
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Categoria creata!");
      }
      
      handleCloseDialog();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!categoryToDelete) return;
    
    setSaving(true);
    try {
      await axios.delete(
        `${API}/admin/vehicle-categories/${categoryToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Categoria eliminata!");
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (category) => {
    try {
      await axios.put(
        `${API}/admin/vehicle-categories/${category.id}`,
        { is_active: !category.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(category.is_active ? "Categoria disattivata" : "Categoria attivata");
      fetchCategories();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  // Group categories by main category
  const groupedCategories = MAIN_CATEGORIES.map(mainCat => ({
    ...mainCat,
    items: categories.filter(c => c.category === mainCat.id)
  }));

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Gestione <span className="text-primary">Categorie Veicoli</span>
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Aggiungi e modifica le tipologie di veicoli disponibili per il noleggio
            </p>
          </div>
          <Button onClick={() => handleOpenDialog()} className="btn-primary">
            <Plus className="mr-2 h-4 w-4" />
            Nuova Categoria
          </Button>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Gestione Categorie:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Le categorie attive saranno visibili alle aziende nel tariffario</li>
              <li>Puoi aggiungere Auto, SUV, Moto o qualsiasi altra tipologia</li>
              <li>Non puoi eliminare categorie già associate a veicoli (disattivale invece)</li>
              <li>L'ordine determina la posizione nella tabella tariffe</li>
            </ul>
          </div>
        </div>

        {/* Categories Table by Group */}
        {groupedCategories.map(group => (
          <div key={group.id} className="card-dashboard">
            <div className="flex items-center gap-2 mb-4 pb-2 border-b">
              <group.icon className="h-5 w-5 text-primary" />
              <h2 className="font-semibold text-lg">{group.label}</h2>
              <span className="text-sm text-muted-foreground">
                ({group.items.length} {group.items.length === 1 ? 'categoria' : 'categorie'})
              </span>
            </div>
            
            {group.items.length === 0 ? (
              <p className="text-muted-foreground text-sm py-4 text-center">
                Nessuna categoria in questa sezione
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>ID</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Descrizione</TableHead>
                    <TableHead className="text-center">Stato</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {group.items.sort((a, b) => a.order - b.order).map((category, idx) => (
                    <TableRow key={category.id} className={!category.is_active ? "opacity-50" : ""}>
                      <TableCell className="text-muted-foreground">{category.order || idx + 1}</TableCell>
                      <TableCell className="font-mono text-sm">{category.id}</TableCell>
                      <TableCell className="font-medium">{category.label}</TableCell>
                      <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">
                        {category.description || "-"}
                      </TableCell>
                      <TableCell className="text-center">
                        <button
                          onClick={() => handleToggleActive(category)}
                          className="inline-flex items-center"
                        >
                          {category.is_active ? (
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                          ) : (
                            <XCircle className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleOpenDialog(category)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => {
                              setCategoryToDelete(category);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        ))}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>
                {editingCategory ? "Modifica Categoria" : "Nuova Categoria"}
              </DialogTitle>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="id">ID (univoco, senza spazi)</Label>
                <Input
                  id="id"
                  placeholder="es: auto_lusso, suv, berlina"
                  value={formData.id}
                  onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                  disabled={!!editingCategory}
                  className={editingCategory ? "bg-muted" : ""}
                />
                {editingCategory && (
                  <p className="text-xs text-muted-foreground">L'ID non può essere modificato</p>
                )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="label">Nome Visualizzato</Label>
                <Input
                  id="label"
                  placeholder="es: Auto di Lusso, SUV, Berlina"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="description">Descrizione (opzionale)</Label>
                <Input
                  id="description"
                  placeholder="es: Veicoli premium per occasioni speciali"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="category">Categoria Principale</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MAIN_CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="order">Ordine</Label>
                <Input
                  id="order"
                  type="number"
                  min="1"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="is_active">Attiva</Label>
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleCloseDialog}>
                Annulla
              </Button>
              <Button onClick={handleSave} disabled={saving} className="btn-primary">
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    {editingCategory ? "Salva Modifiche" : "Crea Categoria"}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle>Conferma Eliminazione</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p>
                Sei sicuro di voler eliminare la categoria <strong>{categoryToDelete?.label}</strong>?
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Questa azione non può essere annullata. Se ci sono veicoli associati, la categoria non potrà essere eliminata.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Annulla
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDelete}
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Elimina"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
